import React, { useEffect, useState } from "react";
import "./StepInTrade_v2.scss";
import { moodQuick, moodPositive, moodNegative, moodDanger } from "mock/journalDay.data";
import { CollapsibleSection, StatusChip, C } from "../../components/ui";
import { getLiveTradeData, getTradePositionHistory } from "./function";
import { DayDetailTracker, PositionTimeLine, TradeExecution } from "./type";
import { RotateCcw, Save } from "lucide-react";
import { Modal } from "antd";
import { t } from 'i18next';
import { API_URL } from "env";
import { CATEGORY_MAP, getEmotionsFromFormConfig, getReTradeDataFromJournal } from "./utility_function";
import { matchesPair, pairLabel, OTHER_PAIR } from "./pairs";

interface TimelineItem {
    time: string;
    tag: string;
    tone: string;
    text: string;
}

interface Trade {
    id: string;
    positionId: string;
    ticket: string;
    symbol: string;
    type: "BUY" | "SELL";
    lot: string;
    entry: string;
    sl: string;
    tp: string;
    risk: string;
    pips?: string;
    expectedRR: string;
    currentRR: string;
    currentPL: string;
    holdingTime: string;
    toTP: string;
    toSL: string;
    progress: number;
    checklist: { id: string; checked: boolean }[];
    emotions: string[];
    moodMode: 'quick' | 'full';
    notes: string;
    pipTp?: string;
    pipSl?: string;
    planReason?: string;
    planLevels?: string;
    executionId?: number;
}

interface LiveTradeItem {
    id: string | number;
    position_id?: string | number;
    account_number?: string | number;
    symbol?: string;
    profit?: number | string;
    position?: {
        id?: number | string;
        symbol?: string;
        side?: string;
        entry_time?: string;
        entry_price?: number;
        stop_loss?: number;
        take_profit?: number;
        volume_units?: number;
        volume_lots?: number;
        pips?: number;
        profit?: number;
        label?: string | null;
    };
    sl?: number | string;
    tp?: number | string;
    stop_loss?: number | string;
    take_profit?: number | string;
    stopLoss?: number | string;
    takeProfit?: number | string;
    entry_price?: number | string;
    open_price?: number | string;
    price?: number | string;
}

const CHECKLIST_LABELS = [
    { id: '17', label: 'Không dời Stop Loss' },
    { id: '18', label: 'Không chốt non vì sợ' },
    { id: '19', label: 'Tuân thủ kế hoạch quản lý vị thế' },
    { id: '20', label: 'Chấp nhận kết quả giao dịch' },
];

const getDefaultChecklist = () => CHECKLIST_LABELS.map((item) => ({ id: item.id, checked: false }));

function parsePosition(positionRaw?: string) {
    if (!positionRaw || positionRaw === "undefined") {
        return null;
    }

    try {
        return JSON.parse(positionRaw);
    } catch (error) {
        console.error("Failed to parse position JSON", error);
        return null;
    }
}

function formatPrice(value: unknown) {
    if (value === null || value === undefined || value === "" || Number(value) === 0) {
        return "-";
    }

    const num = Number(value);
    if (Number.isNaN(num)) {
        return String(value);
    }

    return num.toString();
}

function formatSignedUSD(value: unknown) {
    const num = Number(value || 0);
    if (Number.isNaN(num)) {
        return "0.00 USD";
    }

    const sign = num > 0 ? "+" : "";
    return `${sign}${num.toFixed(2)} USD`;
}

function formatTimelineItem(item: PositionTimeLine | any): TimelineItem {
    const rawTime = item.time || item.data?.time || item.created_at || "";

    // --- XỬ LÝ LẤY THỜI GIAN CHÍNH XÁC ---
    let timeFormatted = "--:--";

    if (typeof rawTime === "string" && rawTime.trim()) {
        const match = rawTime.match(/(\d{1,2}:\d{2})/);
        if (match) {
            const [hh, mm] = match[1].split(":");
            timeFormatted = `${hh.padStart(2, "0")}:${mm}`;
        }
    } else if (rawTime) {
        const dateObj = new Date(rawTime);
        if (!isNaN(dateObj.getTime())) {
            const hh = String(dateObj.getHours()).padStart(2, "0");
            const mm = String(dateObj.getMinutes()).padStart(2, "0");
            timeFormatted = `${hh}:${mm}`;
        }
    }

    const tag = (item.type || item.event || item.data?.event || "LOG").toUpperCase();

    const hasSL = item.data?.new_sl !== undefined;
    const hasTP = item.data?.new_tp !== undefined;

    const parts: string[] = [];
    if (hasSL) {
        parts.push(`SL: ${formatPrice(item.data.old_sl || 0)} ➔ ${formatPrice(item.data.new_sl || 0)}`);
    }
    if (hasTP) {
        parts.push(`TP: ${formatPrice(item.data.old_tp || 0)} ➔ ${formatPrice(item.data.new_tp || 0)}`);
    }

    let tone = "info";
    if (tag.includes("OPEN")) {
        tone = "open";
    } else if (hasSL && hasTP) {
        tone = "sltp";
    } else if (hasSL || tag.includes("SL")) {
        tone = "sl";
    } else if (hasTP || tag.includes("TP")) {
        tone = "tp";
    } else if (tag.includes("CLOSE")) {
        tone = "close";
    }   

    let text = item.data?.event || item.text || item.message || "";

    // Xử lý riêng cho sự kiện OPEN - Hiển thị giá mở lệnh
    if (tag.includes("OPEN")) {
        const side = item.data?.side && item.data.side !== "UNKNOWN" ? item.data.side : "";
        const openPrice = item.data?.open_price ?? item.data?.price ?? item.open_price;
        const formattedOpenPrice = openPrice && Number(openPrice) > 0 ? formatPrice(openPrice) : "-";

        const openDetails = [side, formattedOpenPrice ? `${formattedOpenPrice}` : ""].filter(Boolean).join(" ");
        text = openDetails ? `Mở vị thế ${openDetails}` : "Mở vị thế";
    } else if (parts.length > 0) {
        text = `Dời ${parts.join(" | ")}`;
    }

    return {
        time: timeFormatted,
        tag,
        tone,
        text: text || tag,
    };
}

function getExecutionForTrade(
    tr: LiveTradeItem,
    journalData: any,
    symbol: string,
    accountNumber: string
): TradeExecution | null {
    if (!journalData?.trade_executions || !Array.isArray(journalData.trade_executions)) {
        return null;
    }

    const positionData = tr.position;

    const targetPositionId = String(tr.position_id || positionData?.id || "");

    const currentAccount = String(accountNumber || tr.account_number || "");

    if (!targetPositionId || !currentAccount) {
        return null;
    }

    return journalData.trade_executions.find((execution: TradeExecution) => {
        const isPositionMatch = String(execution.position_id) === targetPositionId;
        const isAccountMatch = String(execution.account_number) === currentAccount;

        return isPositionMatch && isAccountMatch;
    }) || null;
}

function mapLiveTradeToCard(
    tr: LiveTradeItem,
    execution: TradeExecution | null,
    prevTrade?: Trade
): Trade {
    const positionData = (tr.position);
    const side = String(positionData?.side || execution?.direction || "BUY").toUpperCase() === "SELL" ? "SELL" : "BUY";

    const entryPrice = positionData?.entry_price ?? execution?.entry_price;

    const rawSl = positionData?.stop_loss
        ?? tr?.stop_loss
        ?? tr?.sl
        ?? tr?.stopLoss
        ?? execution?.sl_price;

    const rawTp = positionData?.take_profit
        ?? tr?.take_profit
        ?? tr?.tp
        ?? tr?.takeProfit
        ?? execution?.tp_price;

    const slPrice = (rawSl !== null && rawSl !== undefined && Number(rawSl) !== 0) ? rawSl : execution?.sl_price;
    const tpPrice = (rawTp !== null && rawTp !== undefined && Number(rawTp) !== 0) ? rawTp : execution?.tp_price;

    const rawPips = positionData?.pips;
    let formattedPips = "-";

    if (rawPips !== undefined && rawPips !== null) {
        const numPips = Number(rawPips);
        if (!isNaN(numPips)) {
            const sign = numPips > 0 ? "+" : "";
            formattedPips = `${sign}${numPips.toFixed(1)}`; // Kết quả dạng: +401.0
        }
    }

    const lotSize = Number(positionData?.volume_lots ?? execution?.lot_size ?? 0);
    const positionId = String(tr.position_id || positionData?.id || execution?.position_id || tr.id);

    const formattedEntry = formatPrice(entryPrice);
    const formattedSl = formatPrice(slPrice);
    const formattedTp = formatPrice(tpPrice);

    const numEntry = Number(entryPrice);
    const numSl = Number(slPrice);
    const numTp = Number(tpPrice);

    let expectedRR = "-";
    let pipSl = "-";
    let pipTp = "-";

    if (!isNaN(numEntry) && numEntry > 0) {
        const sym = String(positionData?.symbol || execution?.symbol || tr.symbol || "").toUpperCase();
        const isJpy = sym.includes("JPY");
        const isGold = sym.includes("XAU") || sym.includes("GOLD");

        let factor = 10000;
        if (isJpy) factor = 100;
        else if (isGold) factor = 10;
        else if (numEntry > 500) factor = 1;

        if (!isNaN(numSl) && numSl > 0) {
            const pips = Math.abs(numEntry - numSl) * factor;
            pipSl = `-${pips.toFixed(1)}`;
        }
        if (!isNaN(numTp) && numTp > 0) {
            const pips = Math.abs(numTp - numEntry) * factor;
            pipTp = `+${pips.toFixed(1)}`;
        }

        if (!isNaN(numSl) && !isNaN(numTp) && numSl > 0 && numTp > 0) {
            const riskDist = Math.abs(numEntry - numSl);
            const rewardDist = Math.abs(numTp - numEntry);
            if (riskDist > 0) {
                expectedRR = `1:${(rewardDist / riskDist).toFixed(1)}`;
            }
        }
    }

    const preTrade = execution?.pre_trade_data;
    const preRiskPercent = preTrade?.sl_loss_percent ? `${preTrade.sl_loss_percent}%` : "-";
    const planReason = preTrade?.bias_reason || "";
    const planLevels = [
        `Entry ${formattedEntry}`,
        `SL ${formattedSl}`,
        `TP ${formattedTp}`,
        preRiskPercent !== "-" ? `Risk ${preRiskPercent}` : ""
    ].filter(Boolean).join(" · ");

    const inTradeData = execution?.in_trade_data;

    const savedChecklistRecords = (execution as any)?.checklist_records || (execution as any)?.items || [];
    let defaultOrSavedChecklist = getDefaultChecklist();

    if (Array.isArray(savedChecklistRecords) && savedChecklistRecords.length > 0) {
        const checkedMap = new Map<string, boolean>();
        savedChecklistRecords.forEach((rec: any) => {
            const itemId = String(rec.item_id ?? rec.id ?? rec.checklist_item_id);
            const isChecked = Boolean(rec.is_checked ?? rec.checked);
            checkedMap.set(itemId, isChecked);
        });

        defaultOrSavedChecklist = CHECKLIST_LABELS.map((labelItem) => ({
            id: labelItem.id,
            checked: checkedMap.has(String(labelItem.id))
                ? Boolean(checkedMap.get(String(labelItem.id)))
                : false
        }));
    }

    return {
        id: positionId,
        positionId,
        ticket: `#${positionId}`,
        symbol: String(positionData?.symbol || execution?.symbol || tr.symbol || "-"),
        type: side,
        lot: lotSize > 0 ? lotSize.toFixed(2) : "0.00",
        entry: formattedEntry,
        sl: formattedSl,
        tp: formattedTp,
        risk: preRiskPercent,
        expectedRR: expectedRR,
        currentRR: "-",
        currentPL: formatSignedUSD(tr.profit),
        holdingTime: "-",
        pips: formattedPips,
        toTP: "",
        toSL: "",
        progress: 0,
        checklist: prevTrade?.checklist || defaultOrSavedChecklist,
        emotions: prevTrade?.emotions || inTradeData?.in_trade_emotions || [],
        moodMode: prevTrade?.moodMode || 'quick',
        notes: prevTrade?.notes ?? inTradeData?.market_developments ?? inTradeData?.discipline_note ?? "",
        pipTp,
        pipSl,
        planReason,
        planLevels: planLevels || undefined,
        executionId: execution?.id,
    };
}

interface StepInTradeV2Props {
    date: string;
    account_number: string;
    symbol: string;
    journal_data: any;
    dynamic_checklist: any;
    form_configs: any;
    ui_labels: any;
    can_edit?: boolean;
    onReload: () => void;
}

const StepInTradeV2: React.FC<StepInTradeV2Props> = ({ date, account_number, symbol, journal_data, dynamic_checklist, form_configs, ui_labels, can_edit, onReload }) => {
    const [trades, setTrades] = useState<Trade[]>([]);
    // Open positions on other symbols than the selected pair (they are filtered out of the list below)
    const [otherSymbols, setOtherSymbols] = useState<Record<string, number>>({});
    const [timeline, setTimeline] = useState<DayDetailTracker[]>([]);

    const [expandedIds, setExpandedIds] = useState<string[]>([]);
    const [saveStatuses, setSaveStatuses] = useState<Record<string, 'idle' | 'saving' | 'saved' | 'error'>>({});

    // 1. Lấy dữ liệu cảm xúc động từ prop form_configs
    const emotionGroups = React.useMemo(() => {
        return getEmotionsFromFormConfig(form_configs);
    }, [form_configs]);

    // 2. Lấy dữ liệu rút gọn (Quick view): Lấy 2 cảm xúc đầu tiên từ mỗi nhóm
    const quickEmotions = React.useMemo(() => {
        return emotionGroups.flatMap((group) => group.items.slice(0, 2));
    }, [emotionGroups]);

    const resetTrade = (tradeIndex: number) => {
        setTrades((prev) => {
            const next = [...prev];
            next[tradeIndex] = {
                ...next[tradeIndex],
                checklist: getDefaultChecklist(),
                emotions: [],
                notes: ""
            };
            return next;
        });
        const tradeId = trades[tradeIndex]?.id;
        if (tradeId) {
            setSaveStatuses((prev) => ({ ...prev, [tradeId]: 'idle' }));
        }
    };

    const saveTrade = async (trade: Trade, index: number) => {
        const tradeId = trade.id; //[cite: 2]
        setSaveStatuses((prev) => ({ ...prev, [tradeId]: 'saving' })); //[cite: 2]

        try {
            const isFirstTrade = index === 0; //[cite: 2]
            const preTradeData = getReTradeDataFromJournal(journal_data); //[cite: 2]

            // 1. Gom danh sách checklist pretrade nếu không phải trade đầu tiên
            const pretradeItems = (!isFirstTrade && preTradeData?.items)
                ? preTradeData.items.map((item: any) => ({
                    item_id: Number(item.id),
                    is_checked: !!item.is_checked
                }))
                : []; //[cite: 2]

            // 2. Gộp checklist của trade hiện tại và pretradeItems
            const itemsPayload = [
                ...trade.checklist.map((item) => ({
                    item_id: Number(item.id) || item.id,
                    is_checked: !!item.checked
                })), //[cite: 2]
                ...pretradeItems //[cite: 2]
            ];

            // 3. Xây dựng Payload gửi API (Bao gồm danh sách cảm xúc trade.emotions)
            const payload = {
                ...(trade.executionId && isFirstTrade ? { id: trade.executionId } : {}), //[cite: 2]
                journal_id: journal_data?.id, //[cite: 2]
                account_number: account_number || null, //[cite: 2]
                position_id: trade.positionId || null, //[cite: 2]
                symbol: trade.symbol || symbol || null, //[cite: 2]
                direction: trade.type?.toUpperCase() || 'BUY', //[cite: 2]
                status: 'active', //[cite: 2]

                entry_price: trade.entry !== '-' ? Number(trade.entry) : null, //[cite: 2]
                lot_size: trade.lot ? Number(trade.lot) : null, //[cite: 2]
                sl_price: trade.sl !== '-' ? Number(trade.sl) : null, //[cite: 2]
                tp_price: trade.tp !== '-' ? Number(trade.tp) : null, //[cite: 2]

                // Dữ liệu trong lệnh
                in_trade_data: {
                    discipline_note: trade.notes, //[cite: 2]
                    in_trade_emotions: trade.emotions, // Mảng string chứa các cảm xúc đã chọn
                    market_developments: trade.notes //[cite: 2]
                },
                items: itemsPayload //[cite: 2]
            };

            // 4. Gửi request POST tới server
            const res = await fetch(`${API_URL}/api/v1/be/checklist/trade`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem("token")}`,
                }, //[cite: 2]
                body: JSON.stringify(payload) //[cite: 2]
            });

            if (!res.ok) {
                setSaveStatuses((prev) => ({ ...prev, [tradeId]: 'error' })); //[cite: 2]
                throw new Error(`HTTP error! status: ${res.status}`); //[cite: 2]
            } else {
                setSaveStatuses((prev) => ({ ...prev, [tradeId]: 'saved' })); //[cite: 2]
            }

            onReload?.(); //[cite: 2]
        } catch (error) {
            console.error("Failed to save trade:", error); //[cite: 2]
            setSaveStatuses((prev) => ({ ...prev, [tradeId]: 'error' })); //[cite: 2]
        } finally {
            // Reset lại trạng thái save sau 2 giây
            setTimeout(() => {
                setSaveStatuses((prev) => ({ ...prev, [tradeId]: 'idle' })); //[cite: 2]
            }, 2000);
        }
    };

    // Several positions are often opened together (grid / scaling in) and share the same discipline notes:
    // copy this trade's checklist, emotions and note to every open position and save them in one go
    const [savingAll, setSavingAll] = useState(false);
    const saveAllLike = (source: Trade) => {
        Modal.confirm({
            title: `Lưu giống nhau cho cả ${trades.length} lệnh?`,
            content: 'Checklist kỷ luật, cảm xúc và ghi chú của lệnh này sẽ được áp dụng và lưu cho tất cả lệnh đang mở.',
            okText: 'Lưu tất cả',
            cancelText: 'Huỷ',
            onOk: async () => {
                setSavingAll(true);
                const next = trades.map((tr) => ({ ...tr, checklist: source.checklist.map((c) => ({ ...c })), emotions: [...source.emotions], notes: source.notes }));
                setTrades(next);
                try {
                    for (let i = 0; i < next.length; i++) {
                        await saveTrade(next[i], i);
                    }
                } finally {
                    setSavingAll(false);
                }
            },
        });
    };

    const toggleExpand = (tradeId: string) => {
        setExpandedIds(prev =>
            prev.includes(tradeId) ? prev.filter(i => i !== tradeId) : [...prev, tradeId]
        );
    };

    useEffect(() => {
        let isMounted = true;

        const fetchLiveTrades = async () => {
            try {
                const res = await getLiveTradeData(account_number);
                const timelineData = await getTradePositionHistory(account_number);

                if (!isMounted) return;
                setTimeline(timelineData || []);

                let liveTrades: LiveTradeItem[] = [];
                if (Array.isArray(res)) {
                    liveTrades = res;
                } else if (res && Array.isArray(res.data)) {
                    liveTrades = res.data;
                } else if (res && typeof res === 'object') {
                    const possibleData = res.data || res.trades || res.items || res;
                    if (Array.isArray(possibleData)) {
                        liveTrades = possibleData;
                    } else if (typeof possibleData === 'object' && possibleData !== null) {
                        liveTrades = [possibleData];
                    }
                }

                // The live table keeps rows for positions that closed long ago (e.g. last updated in March with an
                // old balance). A really open position is refreshed continuously, so drop rows silent for 7+ days.
                const STALE_MS = 7 * 24 * 60 * 60 * 1000;
                liveTrades = liveTrades.filter((tr: any) => {
                    const updated = tr.updated_at ? new Date(tr.updated_at).getTime() : NaN;
                    return isNaN(updated) || Date.now() - updated < STALE_MS;
                });

                const others: Record<string, number> = {};
                liveTrades.forEach((tr) => {
                    const s = String(tr.position?.symbol || tr.symbol || '').toUpperCase();
                    if (s && !matchesPair(s, symbol)) others[s] = (others[s] || 0) + 1;
                });
                setOtherSymbols(others);

                const tradesBySymbol = liveTrades
                    .filter((tr) => {
                        const positionData = (tr.position);
                        const tradeSymbol = positionData?.symbol || tr.symbol;
                        return matchesPair(tradeSymbol, symbol);
                    })
                    .sort((a, b) => String(a.id || "").localeCompare(String(b.id || "")));

                setTrades((prevTrades) => {
                    const prevById = new Map(prevTrades.map((trade) => [trade.id, trade]));

                    return tradesBySymbol.map((tradeItem) => {
                        const execution = getExecutionForTrade(tradeItem, journal_data, symbol, account_number);
                        const positionData = (tradeItem.position);
                        const tradePositionId = String(tradeItem.position_id || positionData?.id || tradeItem.id);
                        const prevTrade = prevById.get(tradePositionId);
                        return mapLiveTradeToCard(tradeItem, execution, prevTrade);
                    });
                });

                setExpandedIds((prev) => {
                    const availableIds = new Set(
                        tradesBySymbol.map((tradeItem) => {
                            const positionData = (tradeItem.position);
                            return String(tradeItem.position_id || positionData?.id || tradeItem.id);
                        })
                    );
                    const next = prev.filter((id) => availableIds.has(id));
                    if (next.length > 0 || tradesBySymbol.length === 0) {
                        return next;
                    }

                    const firstPosition = (tradesBySymbol[0].position);
                    return [String(tradesBySymbol[0].position_id || firstPosition?.id || tradesBySymbol[0].id)];
                });
            } catch (error) {
                console.error("Failed to fetch live trades", error);
                if (isMounted) {
                    setTrades([]);
                }
            }
        };

        fetchLiveTrades();
        const timer = setInterval(fetchLiveTrades, 10000);

        return () => {
            isMounted = false;
            clearInterval(timer);
        };
    }, [account_number, symbol, journal_data]);

    const toggleEmotion = (tradeIndex: number, emotion: string) => {
        setTrades((prevTrades) => {
            const newTrades = [...prevTrades];
            const currentTrade = { ...newTrades[tradeIndex] };

            if (currentTrade.emotions.includes(emotion)) {
                currentTrade.emotions = currentTrade.emotions.filter((e) => e !== emotion);
            } else {
                currentTrade.emotions = [...currentTrade.emotions, emotion];
            }
            newTrades[tradeIndex] = currentTrade;
            return newTrades;
        });
    };

    const setMoodMode = (tradeIndex: number, mode: 'quick' | 'full') => {
        setTrades(prev => {
            const next = [...prev];
            next[tradeIndex] = { ...next[tradeIndex], moodMode: mode };
            return next;
        });
    };

    const toggleChecklist = (tradeIndex: number, itemId: string) => {
        setTrades((prevTrades) => {
            const newTrades = [...prevTrades];
            const currentTrade = { ...newTrades[tradeIndex] };
            currentTrade.checklist = currentTrade.checklist.map(item =>
                item.id === itemId ? { ...item, checked: !item.checked } : item
            );
            newTrades[tradeIndex] = currentTrade;
            return newTrades;
        });
    };

    const updateNotes = (tradeIndex: number, notes: string) => {
        setTrades((prevTrades) => {
            const newTrades = [...prevTrades];
            newTrades[tradeIndex] = { ...newTrades[tradeIndex], notes };
            return newTrades;
        });
    };

    const panelStyle: React.CSSProperties = { background: 'oklch(99% 0.002 80)', border: '1px solid oklch(91% 0.01 80)', borderRadius: '14px', padding: '16px' };
    const planRefStyle: React.CSSProperties = { background: 'oklch(96% 0.03 65)', border: '1px solid oklch(88% 0.05 60)', borderRadius: '14px', padding: '16px' };

    return (
        <div className="step-in-trade-v2">
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {/* At-a-glance bar: how many positions, floating P&L, and how many run without a stop loss */}
                {trades.length > 0 && (() => {
                    const totalPL = trades.reduce((sum, tr) => sum + (parseFloat(String(tr.currentPL).replace(/[^0-9.-]/g, '')) || 0), 0);
                    const noSl = trades.filter((tr) => tr.sl === '-').length;
                    return (
                        <div className="jd-intrade-summary">
                            <div><span>Lệnh đang mở</span><b>{trades.length}</b></div>
                            <div><span>Lãi/lỗ tạm tính</span><b style={{ color: totalPL >= 0 ? C.green : C.red }}>{totalPL >= 0 ? '+' : ''}{totalPL.toFixed(2)} USD</b></div>
                            <div><span>Chưa đặt SL</span><b style={{ color: noSl > 0 ? C.red : C.green }}>{noSl > 0 ? `${noSl} lệnh` : 'Không'}</b></div>
                        </div>
                    );
                })()}
                {trades.map((trade, index) => {
                    const isExpanded = expandedIds.includes(trade.id);
                    const checklistDoneCount = trade.checklist.filter(i => i.checked).length;
                    const checklistTotalCount = trade.checklist.length;
                    const saveStatus = saveStatuses[trade.id] || 'idle';
                    const cleanPositionId = trade.positionId.replace('#', '').trim();
                    const matchedTracker = timeline.find(
                        t => String(t.tracker_id) === cleanPositionId && String(t.account_number) === String(account_number)
                    );
                    const formattedTimeline: TimelineItem[] = matchedTracker?.timeline?.map(formatTimelineItem) || [];

                    return (
                        <CollapsibleSection
                            key={trade.id}
                            title={
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <span style={{
                                        fontSize: '10px',
                                        fontWeight: 800,
                                        padding: '2px 8px',
                                        borderRadius: '6px',
                                        background: trade.type === 'BUY' ? 'oklch(96% 0.04 145)' : 'oklch(94% 0.04 25)',
                                        color: trade.type === 'BUY' ? 'oklch(50% 0.14 145)' : 'oklch(55% 0.18 25)',
                                        textTransform: 'uppercase'
                                    }}>
                                        {trade.type}
                                    </span>
                                    <span style={{ fontSize: '15.5px', fontWeight: 700, color: 'oklch(20% 0.01 80)', fontFamily: '"JetBrains Mono", monospace' }}>
                                        {trade.symbol}
                                    </span>
                                    <span style={{ fontSize: '11.5px', color: 'oklch(55% 0.01 80)', fontWeight: 400 }}>
                                        {trade.ticket} · Lot {trade.lot}
                                    </span>
                                </div>
                            }
                            open={isExpanded}
                            onToggle={() => toggleExpand(trade.id)}
                            chip={
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                    {/* A running position without a stop loss is the main risk to flag while in the trade */}
                                    {trade.sl === '-' && <StatusChip tone="red" dot>Chưa đặt SL</StatusChip>}
                                    <StatusChip tone={trade.currentPL.startsWith("+") ? "green" : "red"}>
                                        {trade.currentPL}
                                    </StatusChip>
                                </span>
                            }
                        >
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '20px' }}>
                                <div className="jd-intrade-stats" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '10px' }}>
                                    {[
                                        { label: 'Entry', value: trade.entry },
                                        { label: 'SL / TP', value: `${trade.sl} / ${trade.tp}` },
                                        // { label: 'Risk', value: `${(Number(trade.tp) / Number(trade.sl)).toFixed(5)}` },
                                        { label: 'RR Kỳ vọng', value: trade.expectedRR },
                                        { label: '± Pips', value: trade.pips || '-' },
                                    ].map((t, idx) => (
                                        <div key={idx}>
                                            <div style={{ fontSize: '10.5px', color: 'oklch(55% 0.01 80)', textTransform: 'uppercase', letterSpacing: '0.03em', marginBottom: '3px' }}>{t.label}</div>
                                            <div style={{ fontSize: '13.5px', fontWeight: 700, color: 'oklch(20% 0.01 80)', fontFamily: '"JetBrains Mono", monospace' }}>{t.value}</div>
                                        </div>
                                    ))}
                                </div>

                                <div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: 'oklch(55% 0.01 80)', marginBottom: '6px' }}>
                                        <span>Tiến độ tới TP</span>
                                        <span style={{ fontWeight: 700, color: 'oklch(30% 0.01 80)' }}>{trade.progress}%</span>
                                    </div>
                                    <div style={{ height: '6px', background: 'oklch(92% 0.01 80)', borderRadius: '100px', overflow: 'hidden' }}>
                                        <div style={{ height: '100%', width: `${trade.progress}%`, background: 'oklch(62% 0.15 55)', borderRadius: '100px' }}></div>
                                    </div>
                                </div>
                                <div style={panelStyle}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                                        <div style={{ fontSize: '13px', fontWeight: 700, color: 'oklch(22% 0.01 80)' }}>Timeline</div>
                                        <div style={{ fontSize: '11px', color: 'oklch(55% 0.01 80)' }}>auto từ Trade Lawyer</div>
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                        {formattedTimeline.map((ev, idx) => {
                                            const tone = (ev.tone || '').toLowerCase();
                                            const tag = (ev.tag || '').toUpperCase();

                                            let tagBg = 'oklch(94% 0.06 70)';
                                            let tagColor = 'oklch(45% 0.14 55)';

                                            if (tone === 'open' || tag.includes('OPEN')) {
                                                tagBg = 'oklch(95% 0.05 220)';  // Nền xanh dương nhạt
                                                tagColor = 'oklch(45% 0.16 220)'; // Chữ xanh dương
                                            }
                                            else if (tone === 'sl' || (tag.includes('SL') && !tag.includes('TP'))) {
                                                tagBg = 'oklch(94% 0.04 25)';
                                                tagColor = 'oklch(55% 0.18 25)';
                                            }

                                            else if (tone === 'tp' || (tag.includes('TP') && !tag.includes('SL'))) {
                                                tagBg = 'oklch(96% 0.04 145)';
                                                tagColor = 'oklch(50% 0.14 145)';
                                            }
                                            else if (tone === 'sltp' || (tag.includes('SL') && tag.includes('TP'))) {
                                                tagBg = 'oklch(96% 0.06 85)';
                                                tagColor = 'oklch(45% 0.14 85)';
                                            }
                                            else if (tone === 'close' || tag.includes('CLOSE')) {
                                                tagBg = 'oklch(92% 0.02 80)';
                                                tagColor = 'oklch(40% 0.02 80)';
                                            }

                                            return (
                                                <div key={idx} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                                                    <div style={{ fontSize: '12px', color: 'oklch(50% 0.01 80)', fontFamily: '"JetBrains Mono", monospace', flexShrink: 0, width: '42px', paddingTop: '2px' }}>
                                                        {ev.time}
                                                    </div>
                                                    <div style={{ fontSize: '13px', color: 'oklch(30% 0.01 80)', lineHeight: '1.4' }}>
                                                        <span style={{ fontSize: '10.5px', fontWeight: 700, padding: '2px 7px', borderRadius: '5px', background: tagBg, color: tagColor, marginRight: '6px', textTransform: 'uppercase', display: 'inline-block' }}>
                                                            {ev.tag}
                                                        </span>
                                                        {ev.text}
                                                    </div>
                                                </div>
                                            );
                                        })}

                                        {formattedTimeline.length === 0 && (
                                            <div style={{ fontSize: '12px', color: 'oklch(60% 0.01 80)' }}>
                                                Chưa có cập nhật timeline cho vị thế này
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {trade.planReason && (
                                    <div style={planRefStyle}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                                            <span style={{ fontSize: '14px' }}>📋</span>
                                            <span style={{ fontSize: '12px', fontWeight: 700, color: 'oklch(40% 0.1 55)', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Kế hoạch từ Đầu ngày</span>
                                        </div>
                                        <div style={{ fontSize: '13px', color: 'oklch(30% 0.02 60)', lineHeight: '1.5', marginBottom: '8px' }}>
                                            <strong>Lý do vào lệnh:</strong> {trade.planReason}
                                        </div>
                                        <div style={{ fontSize: '12.5px', color: 'oklch(38% 0.03 60)', lineHeight: '1.6', fontFamily: '"JetBrains Mono", monospace' }}>{trade.planLevels}</div>
                                    </div>
                                )}

                                <div style={panelStyle}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                                        <div style={{ fontSize: '13px', fontWeight: 700, color: 'oklch(22% 0.01 80)' }}>Xác nhận kỷ luật giữ lệnh</div>
                                        <div style={{ fontSize: '11.5px', fontWeight: 700, color: 'oklch(48% 0.13 55)' }}>{checklistDoneCount}/{checklistTotalCount}</div>
                                    </div>
                                    {trade.checklist.map((item) => {
                                        const label = CHECKLIST_LABELS.find(l => l.id === item.id)?.label || item.id;
                                        return (
                                            <label key={item.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 4px', cursor: 'pointer' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={item.checked}
                                                    onChange={() => toggleChecklist(index, item.id)}
                                                    style={{ width: '16px', height: '16px', cursor: 'pointer', flexShrink: 0 }}
                                                />
                                                <span style={{ fontSize: '13.5px', color: item.checked ? 'oklch(20% 0.01 80)' : 'oklch(50% 0.01 80)', fontWeight: item.checked ? 600 : 400 }}>{label}</span>
                                            </label>
                                        );
                                    })}
                                </div>

                                <div style={panelStyle}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                                        <div style={{ fontSize: '13px', fontWeight: 700, color: 'oklch(22% 0.01 80)' }}>
                                            Bạn đang cảm thấy gì?
                                        </div>
                                        <div className="jd-seg" style={{ width: "160px" }}>
                                            <button
                                                className={trade.moodMode === "quick" ? "active" : ""}
                                                onClick={() => setMoodMode(index, "quick")}
                                            >
                                                Nhanh
                                            </button>
                                            <button
                                                className={trade.moodMode === "full" ? "active" : ""}
                                                onClick={() => setMoodMode(index, "full")}
                                            >
                                                Đầy đủ
                                            </button>
                                        </div>
                                    </div>

                                    {trade.moodMode === "quick" ? (
                                        /* --- DẠNG CHỌN NHANH --- */
                                        <div className="chip-group">
                                            {quickEmotions.map((m) => {
                                                // Kiểm tra đã chọn theo value hoặc label
                                                const isSelected = trade.emotions.includes(m.value) || trade.emotions.includes(m.label_vi);
                                                const color = CATEGORY_MAP[m.category]?.color || "var(--neutral)";

                                                return (
                                                    <div
                                                        key={m.value}
                                                        className={`chip ${isSelected ? "selected" : ""}`}
                                                        style={{
                                                            borderColor: isSelected ? color : undefined,
                                                            backgroundColor: isSelected ? `${color}1F` : undefined,
                                                            color: isSelected ? color : undefined,
                                                            cursor: 'pointer'
                                                        }}
                                                        onClick={() => toggleEmotion(index, m.value)}
                                                    >
                                                        {m.label_vi}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <div className="mood-full">
                                            {emotionGroups.map((group) => (
                                                <div key={group.categoryKey} style={{ marginBottom: '12px' }}>
                                                    {/* Header Nhóm */}
                                                    <div className="mood-group-label" style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 600, marginBottom: '8px' }}>
                                                        <span className="dot" style={{ width: '8px', height: '8px', borderRadius: '50%', background: group.color }}></span>
                                                        <span style={{ color: 'oklch(35% 0.01 80)' }}>{group.title}</span>
                                                    </div>

                                                    {/* Danh sách Chip thuộc Nhóm */}
                                                    <div className="chip-group" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                                        {group.items.map((m) => {
                                                            const isSelected = trade.emotions.includes(m.value) || trade.emotions.includes(m.label_vi);

                                                            return (
                                                                <div
                                                                    key={m.value}
                                                                    className={`chip ${isSelected ? "selected" : ""}`}
                                                                    style={{
                                                                        borderColor: isSelected ? group.color : undefined,
                                                                        backgroundColor: isSelected ? `${group.color}22` : undefined,
                                                                        color: isSelected ? group.color : undefined,
                                                                        cursor: 'pointer'
                                                                    }}
                                                                    onClick={() => toggleEmotion(index, m.value)}
                                                                >
                                                                    {m.label_vi}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <div style={panelStyle}>
                                    <div style={{ fontSize: '13px', fontWeight: 700, color: 'oklch(22% 0.01 80)', marginBottom: '10px' }}>Ghi chú trong lệnh</div>
                                    <textarea
                                        value={trade.notes}
                                        onChange={(e) => updateNotes(index, e.target.value)}
                                        placeholder="Ghi lại diễn biến tâm lý, lý do điều chỉnh lệnh..."
                                        style={{
                                            width: '100%', minHeight: '80px', boxSizing: 'border-box',
                                            border: '1px solid oklch(89% 0.01 80)', borderRadius: '10px',
                                            padding: '10px 12px', fontSize: '13.5px', color: 'oklch(25% 0.01 80)',
                                            resize: 'vertical', outline: 'none', background: 'oklch(99% 0.002 80)',
                                            fontFamily: 'inherit'
                                        }}
                                    ></textarea>
                                </div>
                            </div>

                            {
                                can_edit ?
                                    (
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginTop: 20, paddingTop: 16, borderTop: `1px dashed ${C.border}` }}>
                                            {trades.length > 1 ? (
                                                <button
                                                    type="button"
                                                    onClick={() => saveAllLike(trade)}
                                                    disabled={savingAll}
                                                    style={{ padding: 0, border: 0, background: 'none', color: C.goldDark, fontFamily: 'inherit', fontSize: 13, fontWeight: 700, cursor: savingAll ? 'not-allowed' : 'pointer' }}
                                                >
                                                    {savingAll ? 'Đang lưu tất cả…' : `Lưu giống nhau cho cả ${trades.length} lệnh`}
                                                </button>
                                            ) : <span />}
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                                <button
                                                    onClick={() => resetTrade(index)}
                                                    disabled={saveStatus === 'saving'}
                                                    style={{
                                                        display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 15px',
                                                        border: `1px solid ${C.neutralBd}`, borderRadius: 10, background: C.surface,
                                                        color: C.muted, fontFamily: 'inherit', fontSize: 13, fontWeight: 700,
                                                        cursor: saveStatus === 'saving' ? 'not-allowed' : 'pointer',
                                                        opacity: saveStatus === 'saving' ? 0.6 : 1
                                                    }}
                                                >
                                                    <RotateCcw size={15} /> {t('Reset')}
                                                </button>

                                                <button
                                                    onClick={() => saveTrade(trade, index)}
                                                    disabled={saveStatus === 'saving' || saveStatus === 'saved'}
                                                    style={{
                                                        display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 15px',
                                                        border: 'none', borderRadius: 10, fontFamily: 'inherit', fontSize: 13, fontWeight: 700,
                                                        transition: 'all 0.3s ease',
                                                        cursor: (saveStatus === 'idle' || saveStatus === 'error') ? 'pointer' : 'not-allowed',
                                                        background: saveStatus === 'saving'
                                                            ? C.neutralBd
                                                            : saveStatus === 'saved'
                                                                ? C.green
                                                                : saveStatus === 'error'
                                                                    ? C.red
                                                                    : C.blue,
                                                        color: C.white,
                                                    }}
                                                >
                                                    {saveStatus === 'saving' && (
                                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" className="jd-spin" style={{ animation: 'jd-keyframes-spin 1s linear infinite' }}>
                                                            <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                                                        </svg>
                                                    )}
                                                    {saveStatus === 'saved' && (
                                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                                            <path d="M20 6L9 17l-5-5" />
                                                        </svg>
                                                    )}
                                                    {saveStatus === 'error' && (
                                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                                            <line x1="18" y1="6" x2="6" y2="18"></line>
                                                            <line x1="6" y1="6" x2="18" y2="18"></line>
                                                        </svg>
                                                    )}
                                                    {saveStatus === 'idle' && <Save size={15} />}

                                                    {saveStatus === 'saving' && t('Saving...')}
                                                    {saveStatus === 'saved' && t('Saved!')}
                                                    {saveStatus === 'error' && t('Error!')}
                                                    {saveStatus === 'idle' && t('Save')}
                                                </button>
                                            </div>
                                        </div>
                                    )
                                    :
                                    (
                                        null
                                    )
                            }
                        </CollapsibleSection>
                    );
                })}

                {trades.length === 0 && (
                    <div style={{ fontSize: '13px', color: 'oklch(55% 0.01 80)', textAlign: 'center', marginTop: '20px' }}>
                        {`Không có giao dịch đang mở cho ${pairLabel(symbol)}`}
                        {Object.keys(otherSymbols).length > 0 && (
                            <div style={{ marginTop: 6, fontWeight: 600, color: 'oklch(45% 0.12 60)' }}>
                                Đang có lệnh mở ở: {Object.entries(otherSymbols).map(([s, n]) => `${s} (${n})`).join(', ')} — đổi "Cặp sản phẩm" phía trên để xem
                                {Object.keys(otherSymbols).some((s) => matchesPair(s, OTHER_PAIR)) ? ` (mã không có trong danh sách: chọn "${OTHER_PAIR}")` : ''}.
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default StepInTradeV2;